import { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, ShieldCheck, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { API } from '../utils/apiConfig';

/**
 * CameraCheckModal
 * Displays a "Face Check" UI step before a driver can start a ride.
 * Uses the device camera to take a snapshot and sends it to the backend.
 */
export const CameraCheckModal = ({ rideId, onSuccess, onClose }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [step, setStep] = useState('intro'); // intro | camera | verifying | success | error | locked
  const [stream, setStream] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [attemptsLeft, setAttemptsLeft] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: 480, height: 480 }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
      setStep('camera');
    } catch {
      setErrorMsg(t('security.cameraCheck.cameraError'));
      setStep('error');
    }
  }, [t]);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  }, [stream]);

  const captureAndVerify = useCallback(async () => {
    // Capture a frame from the live <video> into a hidden <canvas>
    // and send the resulting JPEG (base64) to the backend for AWS Rekognition.
    let photoB64 = null;
    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (video && canvas && video.videoWidth) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        photoB64 = canvas.toDataURL('image/jpeg', 0.85);
      }
    } catch {/* ignore — handled below */}

    setStep('verifying');
    stopCamera();

    if (!photoB64) {
      setErrorMsg(t('security.cameraCheck.cameraError', 'Kamera konnte kein Bild aufnehmen.'));
      setStep('error');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`${API}/verification/face-match`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ride_id: rideId, photo_b64: photoB64 }),
      });
      const data = await resp.json().catch(() => ({}));

      if (resp.ok && data.matched) {
        setStep('success');
        setTimeout(() => onSuccess?.(data), 1500);
        return;
      }

      // Hard-lock after 3 failed attempts
      if (resp.status === 429 || data.error === 'max_attempts_reached' || data.locked) {
        setStep('locked');
        return;
      }

      if (typeof data.remaining === 'number') setAttemptsLeft(data.remaining);

      // Map the backend error codes to i18n-friendly messages
      const codeMap = {
        no_match: t('security.cameraCheck.errNoMatch', 'Gesicht nicht erkannt — bitte nochmal versuchen.'),
        no_face_detected: t('security.cameraCheck.errNoFace', 'Kein Gesicht erkannt. Ins Licht halten.'),
        no_face_source: t('security.cameraCheck.errNoSource', 'Profilbild fehlt — bitte zuerst hochladen.'),
        no_profile_photo: t('security.cameraCheck.errNoSource', 'Profilbild fehlt — bitte zuerst hochladen.'),
        image_quality_poor: t('security.cameraCheck.errQuality', 'Bild zu dunkel/unscharf — bitte nochmal.'),
        invalid_image: t('security.cameraCheck.errInvalid', 'Bild-Format ungültig.'),
        max_attempts_reached: t('security.cameraCheck.errLocked', '3× fehlgeschlagen — bitte Support kontaktieren.'),
        service_unavailable: t('security.cameraCheck.errService', 'Service kurz nicht erreichbar.'),
      };
      setErrorMsg(codeMap[data.error] || data.detail || t('security.cameraCheck.verifyError'));
      setStep('error');
    } catch {
      setErrorMsg(t('security.cameraCheck.verifyError'));
      setStep('error');
    }
  }, [rideId, stopCamera, onSuccess, t]);

  const handleClose = () => {
    stopCamera();
    onClose?.();
  };

  return (
    <div data-testid="camera-check-modal" style={overlayStyle}>
      <div style={modalStyle}>
        <button onClick={handleClose} data-testid="camera-check-close" style={closeBtnStyle}>
          <X size={20} />
        </button>

        {step === 'intro' && (
          <div style={contentStyle}>
            <div style={iconCircleStyle}>
              <ShieldCheck size={40} style={{ color: '#2563eb' }} />
            </div>
            <h2 style={titleStyle}>{t('security.cameraCheck.title')}</h2>
            <p style={descStyle}>{t('security.cameraCheck.description')}</p>
            <button onClick={startCamera} data-testid="camera-check-start" style={primaryBtnStyle}>
              <Camera size={18} />
              {t('security.cameraCheck.startCamera')}
            </button>
          </div>
        )}

        {step === 'camera' && (
          <div style={contentStyle}>
            <h2 style={titleStyle}>{t('security.cameraCheck.positionFace')}</h2>
            <div style={videoContainerStyle}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={videoStyle}
                data-testid="camera-check-video"
              />
              <div style={faceGuideStyle} />
              <canvas ref={canvasRef} style={{ display: 'none' }} width={480} height={480} />
            </div>
            <button onClick={captureAndVerify} data-testid="camera-check-capture" style={primaryBtnStyle}>
              <Camera size={18} />
              {t('security.cameraCheck.capture')}
            </button>
          </div>
        )}

        {step === 'verifying' && (
          <div style={contentStyle}>
            <Loader2 size={48} style={{ color: '#2563eb', animation: 'spin 1s linear infinite' }} />
            <h2 style={titleStyle}>{t('security.cameraCheck.verifying')}</h2>
          </div>
        )}

        {step === 'success' && (
          <div style={contentStyle}>
            <CheckCircle size={56} style={{ color: '#16a34a' }} />
            <h2 style={{ ...titleStyle, color: '#16a34a' }}>{t('security.cameraCheck.success')}</h2>
            <p style={descStyle}>{t('security.cameraCheck.successDesc')}</p>
          </div>
        )}

        {step === 'error' && (
          <div style={contentStyle}>
            <AlertCircle size={48} style={{ color: '#dc2626' }} />
            <h2 style={{ ...titleStyle, color: '#dc2626' }}>{t('security.cameraCheck.errorTitle')}</h2>
            <p style={descStyle}>{errorMsg}</p>
            {attemptsLeft !== null && attemptsLeft > 0 && (
              <p style={{ ...descStyle, color: '#f59e0b', fontWeight: 600 }}>
                {t('security.cameraCheck.attemptsLeft', { count: attemptsLeft,
                   defaultValue: 'Noch {{count}} Versuch(e) — danach wird gesperrt.' })}
              </p>
            )}
            <button
              data-testid="camera-check-retry"
              onClick={() => { setStep('intro'); setErrorMsg(''); }}
              style={primaryBtnStyle}
            >
              {t('security.cameraCheck.retry')}
            </button>
          </div>
        )}

        {step === 'locked' && (
          <div style={contentStyle}>
            <AlertCircle size={48} style={{ color: '#b91c1c' }} />
            <h2 style={{ ...titleStyle, color: '#b91c1c' }}>
              {t('security.cameraCheck.lockedTitle', 'Zu viele Fehlversuche')}
            </h2>
            <p style={descStyle}>
              {t('security.cameraCheck.lockedDesc',
                 'Aus Sicherheitsgründen hast du 3× nicht gematcht. Unser Support hilft dir persönlich weiter.')}
            </p>
            <a
              href="/support?topic=face-match"
              data-testid="camera-check-support"
              style={{ ...primaryBtnStyle, background: '#b91c1c', textDecoration: 'none' }}
            >
              {t('security.cameraCheck.contactSupport', 'Support kontaktieren')}
            </a>
            <button
              data-testid="camera-check-close-locked"
              onClick={handleClose}
              style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 14 }}
            >
              {t('common.cancel', 'Schließen')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const overlayStyle = {
  position: 'fixed', inset: 0, zIndex: 99999,
  background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '1rem',
};
const modalStyle = {
  background: '#fff', borderRadius: '20px', padding: '2rem',
  maxWidth: '420px', width: '100%', position: 'relative',
  boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
};
const closeBtnStyle = {
  position: 'absolute', top: '1rem', right: '1rem',
  background: '#f1f5f9', border: 'none', borderRadius: '50%',
  width: '36px', height: '36px', display: 'flex', alignItems: 'center',
  justifyContent: 'center', cursor: 'pointer', color: '#64748b',
};
const contentStyle = {
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  gap: '1rem', textAlign: 'center',
};
const iconCircleStyle = {
  width: '80px', height: '80px', borderRadius: '50%',
  background: '#eff6ff', display: 'flex', alignItems: 'center',
  justifyContent: 'center',
};
const titleStyle = {
  fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', margin: 0,
};
const descStyle = {
  fontSize: '0.9rem', color: '#64748b', lineHeight: 1.6, margin: 0,
};
const primaryBtnStyle = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.85rem 2rem', background: '#2563eb', color: '#fff',
  border: 'none', borderRadius: '12px', fontWeight: 700,
  fontSize: '1rem', cursor: 'pointer', transition: 'background 0.2s',
};
const videoContainerStyle = {
  position: 'relative', width: '260px', height: '260px',
  borderRadius: '50%', overflow: 'hidden', border: '4px solid #2563eb',
  margin: '0.5rem 0',
};
const videoStyle = {
  width: '100%', height: '100%', objectFit: 'cover',
};
const faceGuideStyle = {
  position: 'absolute', inset: '15%',
  border: '2px dashed rgba(37, 99, 235, 0.5)', borderRadius: '50%',
  pointerEvents: 'none',
};
